import {
  BarChart2,
  RefreshCw,
  Download,
  FileText,
  FileSpreadsheet,
  IndianRupee,
  TrendingUp,
  Users,
  Receipt,
  CheckCircle,
  AlertCircle,
  Loader2,
  CalendarDays,
  BadgeCheck,
} from "lucide-react";
import { useBusinessReport } from "../../hooks/useBusinessReport";
import Pagination from "../../components/tables/Pagination";
import DateRangePicker from "../../components/ui/DateRangePicker";

// ─── helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const formatDate = (dateString) =>
  new Date(dateString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

// ─── sub-components ─────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, iconBg, textColor }) {
  return (
    <div className="theme-card rounded-lg p-5 flex items-start space-x-3">
      <div className={`${iconBg} p-3 rounded-lg flex-shrink-0`}>
        <Icon className={`h-6 w-6 ${textColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 font-medium line-clamp-2">{label}</p>
        <p className={`text-lg font-bold ${textColor} line-clamp-2 mt-1`}>{value}</p>
      </div>
    </div>
  );
}

function SettlementBadge({ status }) {
  const isSettled = status === "Settled";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border
        ${isSettled
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-yellow-50 text-yellow-700 border-yellow-200"
        }`}
    >
      {isSettled ? (
        <BadgeCheck className="h-3.5 w-3.5" />
      ) : (
        <AlertCircle className="h-3.5 w-3.5" />
      )}
      {status}
    </span>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export default function BusinessReports() {
  const {
    transactions,
    pagination,
    filter,
    stats,
    summaryTotals,
    loading,
    error,
    exportLoading,
    filters,
    updateFilter,
    handlePageChange,
    selectedMonthLabel,
    MONTHS,
    YEARS,
    getAvailableMonths,
    handleExportXlsx,
    handleExportPdf,
    refreshReport,
  } = useBusinessReport();

  // ── summary cards config ────────────────────────────────────────────────
  const cards = [
    {
      icon: Receipt,
      label: "Total Transactions",
      value: stats?.total_transactions || 0,
      iconBg: "bg-blue-100",
      textColor: "text-blue-600",
    },
    {
      icon: IndianRupee,
      label: "Total Revenue",
      value: formatCurrency(stats?.total_revenue || 0),
      iconBg: "bg-purple-100",
      textColor: "text-purple-600",
    },
    {
      icon: Users,
      label: "Customer Savings",
      value: formatCurrency(stats?.customer_savings || 0),
      iconBg: "bg-green-100",
      textColor: "text-green-600",
    },
    {
      icon: TrendingUp,
      label: "Platform Commission",
      value: formatCurrency(stats?.platform_commission || 0),
      iconBg: "bg-orange-100",
      textColor: "text-orange-600",
    },
    {
      icon: CheckCircle,
      label: "Net Earnings",
      value: formatCurrency(stats?.net_earnings || 0),
      iconBg: "bg-emerald-100",
      textColor: "text-emerald-600",
    },
  ];

  // ── render ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="theme-card rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BarChart2 className="h-7 w-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
              <p className="text-sm text-gray-500">
                Monthly transaction summary &amp; financials
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {cards.map((card, index) => (
          <SummaryCard key={index} {...card} />
        ))}
      </div>

      {/* ── Table with integrated filters and controls ── */}
      <div className="theme-card rounded-lg overflow-visible">
        {/* Table header with filters, date range, and export buttons */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 relative z-10">
          {/* Filter Label */}
          <div className="flex items-center gap-2 text-gray-600 mb-3">
            <CalendarDays className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs font-medium uppercase whitespace-nowrap">Filter by:</span>
          </div>

          {/* Filters Row 1 - Year, Month, DatePicker */}
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            {/* Year Selector */}
            <select
              value={filters.year}
              onChange={(e) => {
                const newYear = Number(e.target.value);
                updateFilter("year", newYear);
                const availableMonths = getAvailableMonths(newYear);
                if (!availableMonths.find((m) => m.value === filters.month)) {
                  updateFilter("month", availableMonths[availableMonths.length - 1].value);
                }
              }}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            {/* Month Selector */}
            <select
              value={filters.month}
              onChange={(e) => updateFilter("month", Number(e.target.value))}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {getAvailableMonths(filters.year).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            {/* Date Range Picker */}
            <div className="flex-1 sm:flex-none">
              <DateRangePicker
                year={filters.year}
                month={filters.month}
                fromDate={filters.from_date}
                toDate={filters.to_date}
                onFromDateChange={(date) => updateFilter("from_date", date)}
                onToDateChange={(date) => updateFilter("to_date", date)}
              />
            </div>
          </div>

          {/* Filters Row 2 - Reset and Export Buttons */}
          <div className="flex flex-wrap gap-2 items-center justify-between">
            {/* Reset Button */}
            <button
              onClick={() => {
                const monthStart = new Date(Date.UTC(filters.year, filters.month - 1, 1));
                const monthEnd = new Date(Date.UTC(filters.year, filters.month, 0));
                const fromDateStr = `${monthStart.getUTCFullYear()}-${String(monthStart.getUTCMonth() + 1).padStart(2, '0')}-${String(monthStart.getUTCDate()).padStart(2, '0')}`;
                const toDateStr = `${monthEnd.getUTCFullYear()}-${String(monthEnd.getUTCMonth() + 1).padStart(2, '0')}-${String(monthEnd.getUTCDate()).padStart(2, '0')}`;
                updateFilter("from_date", fromDateStr);
                updateFilter("to_date", toDateStr);
              }}
              className="px-3 py-2 rounded border border-gray-300 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>

            {/* Export and Refresh Buttons */}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={handleExportXlsx}
                disabled={exportLoading || loading || transactions.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 rounded border border-green-600 text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
              >
                {exportLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Excel</span>
              </button>

              <button
                onClick={handleExportPdf}
                disabled={exportLoading || loading || transactions.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 rounded border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
              >
                {exportLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">PDF</span>
              </button>

              <button
                onClick={refreshReport}
                disabled={loading}
                title="Refresh"
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Table title section */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-base font-semibold text-gray-900">
            Transaction Details
            {pagination && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({pagination.total} total)
              </span>
            )}
          </h2>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span>Loading report…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600">Failed to load report data.</p>
            <button
              onClick={refreshReport}
              className="theme-button-primary px-4 py-2 rounded-lg text-sm"
            >
              Try Again
            </button>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <BarChart2 className="h-12 w-12 text-gray-300" />
            <p className="text-gray-500 font-medium">
              No transactions for {selectedMonthLabel} {filters.year}
            </p>
            <p className="text-gray-400 text-sm">
              Try selecting a different month or year.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "#",
                    "Reference ID",
                    "Customer",
                    "Mobile",
                    "Bill Amount",
                    "Customer Discount",
                    "Final Amount",
                    "Date & Time",
                    "Platform Commission",
                    "Settlement",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {transactions.map((t) => (
                  <tr
                    key={t.reference_id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* Sl no */}
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                      {t.sl_no}
                    </td>

                    {/* Reference ID */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                        {t.reference_id}
                      </span>
                    </td>

                    {/* Customer name */}
                    <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                      {t.customer_name || "N/A"}
                    </td>

                    {/* Mobile */}
                    <td className="px-4 py-3 text-gray-600 font-mono">
                      {t.mobile}
                    </td>

                    {/* Bill Amount */}
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(t.bill_amount)}
                    </td>

                    {/* Customer Discount */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-green-700 font-medium">
                        {formatCurrency(t.customer_discount)}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">
                        (
                        {t.bill_amount
                          ? ((t.customer_discount / t.bill_amount) * 100).toFixed(1)
                          : 0}
                        %)
                      </span>
                    </td>

                    {/* Final Amount */}
                    <td className="px-4 py-3 font-bold text-emerald-700 whitespace-nowrap">
                      {formatCurrency(t.final_amount)}
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {formatDate(t.date_time)}
                    </td>

                    {/* Platform Commission */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-orange-600 font-medium">
                        {formatCurrency(t.platform_commission)}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">
                        (
                        {t.bill_amount
                          ? ((t.platform_commission / t.bill_amount) * 100).toFixed(1)
                          : 0}
                        %)
                      </span>
                    </td>

                    {/* Settlement */}
                    <td className="px-4 py-3">
                      <SettlementBadge status={t.settlement_status} />
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* Totals footer row */}
              {transactions.length > 0 && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-3 text-sm font-semibold text-gray-700"
                    >
                      Page Total
                    </td>
                    <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">
                      {formatCurrency(summaryTotals.totalBillAmount)}
                    </td>
                    <td className="px-4 py-3 font-bold text-green-700 whitespace-nowrap">
                      {formatCurrency(summaryTotals.totalCustomerDiscount)}
                    </td>
                    <td className="px-4 py-3 font-bold text-emerald-700 whitespace-nowrap">
                      {formatCurrency(summaryTotals.totalFinalAmount)}
                    </td>
                    <td colSpan={1} />
                    <td className="px-4 py-3 font-bold text-orange-600 whitespace-nowrap">
                      {formatCurrency(summaryTotals.totalPlatformCommission)}
                    </td>
                    <td colSpan={1} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.last_page}
            totalItems={pagination.total}
            itemsPerPage={pagination.per_page}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
